import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Bot, Pencil, Trash2 } from 'lucide-react';
import { TASK_STATUSES, deleteTask, taskHistory, updateTask } from '@mkcore/projects/schema';
import { Button, Card, ErrorState, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { ConfirmButton } from '@/components/confirm';
import { Markdown } from '@/components/markdown';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { Checklist, DueDate, PriorityBadge, Subtasks, TaskDialog, TaskParentLink, TaskStatusBadge, taskQuery } from '@/features/projects/tasks';
import { TaskDependencies } from '@/features/projects/dependencies';
import { TaskTime } from '@/features/projects/task-time';
import { HandToAgentDialog, TaskAgentPanel } from '@/features/projects/task-agent-panel';
import { ItemAgent } from '@/features/agents/item-status';
import { Comments } from '@/features/collab/comments';
import { History } from '@/components/history';
import { call } from '@/lib/api';

export const Route = createFileRoute('/_app/tasks/$id')({ component: TaskPage });

/**
 * One task (CO-0435): status, owner, priority, due date, time against the estimate, checklist, links and comments
 * (CO-0552). The next step on top, and with an agent as owner the agent panel; a person gives the task to an agent
 * with a next step (CO-1377, ADR-0032). A task has subtasks, or a subtask its parent (CO-0443).
 */
function TaskPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const canUpdate = useCan('projects:update');
  const canDelete = useCan('projects:delete');
  const canAgents = useCan('agents:read');
  const [editing, setEditing] = useState(false);
  const [handing, setHanding] = useState(false);
  const q = useQuery(taskQuery(id));
  const status = useActionMutation(updateTask, { invalidate: [['project', 'task']] });
  const remove = useActionMutation(deleteTask, { invalidate: [['project', 'task']], success: t('tasks.deleted'), onSuccess: () => void navigate({ to: '/tasks' }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const task = q.data;
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
      <dt className="text-fg-3">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
  const links = (['lead', 'proposal', 'agreement', 'invoice'] as const).filter((k) => task[k] && !(k === 'lead' && task.parent.type === 'lead'));
  return (
    <>
      <Link to="/tasks" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('tasks.title')}
      </Link>
      <PageHeader
        eyebrow={task.key}
        title={task.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <TaskStatusBadge status={task.status} />
            <PriorityBadge priority={task.priority} />
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canUpdate && (
              <NativeSelect
                aria-label={t('tasks.fields.status')}
                className="h-9 w-40"
                value={task.status}
                disabled={status.isPending}
                onChange={(e) => status.mutate({ input: { id: task.id, status: e.target.value as never }, ifMatch: task.version })}
              >
                {TASK_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {t(`tasks.status.${st}`)}
                  </option>
                ))}
              </NativeSelect>
            )}
            {canUpdate && canAgents && (task.status === 'todo' || task.status === 'in_progress') && (
              <Button variant="secondary" onClick={() => setHanding(true)}>
                <Bot /> {t('tasks.agent.handOver')}
              </Button>
            )}
            {canUpdate && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                <Pencil /> {t('common.edit')}
              </Button>
            )}
            {canDelete && (
              <ConfirmButton
                icon={<Trash2 />}
                label={t('common.delete')}
                title={t('tasks.deleteTitle', { key: task.key })}
                description={t('tasks.deleteText')}
                variant="danger"
                onConfirm={() => remove.mutateAsync({ input: { id: task.id }, ifMatch: task.version })}
              />
            )}
          </div>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card className="grid content-start gap-4 p-4">
          {task.nextAction && (
            <section className="rounded-md border border-primary/40 bg-surface-alt p-3" aria-label={t('tasks.fields.nextAction')}>
              <h2 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('tasks.fields.nextAction')}</h2>
              <Markdown text={task.nextAction} />
            </section>
          )}
          {task.description ? <Markdown text={task.description} /> : <p className="text-sm text-fg-3">{t('tasks.noDescription')}</p>}
          <div>
            <h2 className="mb-2 text-base font-bold">
              {t('tasks.checklist')} {task.progress !== null && <span className="text-sm font-normal text-fg-3">{task.progress}%</span>}
            </h2>
            <Checklist task={task} />
          </div>
          <Subtasks task={task} />
          <TaskDependencies task={task} />
          <Comments type="task" id={task.id} />
        </Card>
        <Card className="grid content-start gap-3 p-4">
          <ItemAgent type="task" id={task.id} />
          {task.ownerKind === 'agent' && <TaskAgentPanel task={task} />}
          <dl className="grid gap-2">
            <Row label={t('tasks.fields.on')}>
              <TaskParentLink parent={task.parent} />
            </Row>
            {task.engagementId && task.parent.type !== 'engagement' && (
              <Row label={t('tasks.on.engagement')}>
                <TaskParentLink bare parent={{ type: 'engagement', id: task.engagementId, label: task.engagementName }} />
              </Row>
            )}
            {task.organisationId && task.parent.type !== 'organisation' && (
              <Row label={t('tasks.on.organisation')}>
                <TaskParentLink bare parent={{ type: 'organisation', id: task.organisationId, label: task.organisationName }} />
              </Row>
            )}
            {task.parentTaskId && (
              <Row label={t('tasks.fields.parent')}>
                <Link to="/tasks/$id" params={{ id: task.parentTaskId }} className="hover:underline">
                  {task.parentKey} {task.parentTitle}
                </Link>
              </Row>
            )}
            {task.milestoneTitle && <Row label={t('tasks.fields.milestone')}>{task.milestoneTitle}</Row>}
            <Row label={t('tasks.fields.owner')}>{task.ownerName ?? t('tasks.nobody')}</Row>
            {task.requestedByName && <Row label={t('tasks.fields.requestedBy')}>{task.requestedByName}</Row>}
            <Row label={t('tasks.fields.dueOn')}>
              <DueDate task={task} />
            </Row>
            <Row label={t('tasks.fields.time')}>
              <TaskTime task={task} />
            </Row>
            {links.map((k) => (
              <Row key={k} label={t(`tasks.links.${k}`)}>
                {task[k]!.label ?? '–'}
              </Row>
            ))}
            {task.doneAt && <Row label={t('tasks.fields.doneAt')}>{formatDateTime(task.doneAt)}</Row>}
            {task.clientTask && <Row label={t('tasks.fields.client')}>{task.clientDoneByName ? t('tasks.doneByClient', { name: task.clientDoneByName }) : t('tasks.forClient')}</Row>}
            {task.clientNote && <Row label={t('tasks.fields.clientNote')}>{task.clientNote}</Row>}
          </dl>
          <History
            queryKey={['project', 'task', task.id]}
            fetchPage={(cursor) => call(taskHistory, { id: task.id, ...(cursor && { cursor }) })}
            valueLabel={(field, v) => (field === 'status' ? t(`tasks.status.${v}`) : field === 'priority' ? t(`tasks.priority.${v}`) : undefined)}
          />
        </Card>
      </div>
      {editing && <TaskDialog task={task} onClose={() => setEditing(false)} />}
      {handing && <HandToAgentDialog task={task} onClose={() => setHanding(false)} />}
    </>
  );
}
